import { Location } from "history";
import isNumber from "lodash-es/isNumber";
import { Reducer } from "redux";

import {
    BAZAAR_NAVIGATION_ACTIVATE,
    BAZAAR_NAVIGATION_STORE_BACK,
    BazaarNavigationActivateAction,
    BazaarNavigationStoreBackAction,
} from "@/mk2/apps/bazaar/actions";
import { ITEMS_PER_PAGE } from "@/mk2/apps/bazaar/constants";
import {
    BAZAAR_GRID_LOAD_API_SUCCESS,
    BazaarGridLoadApiSuccessAction,
} from "@/mk2/apps/bazaar/containers/BazaarGrid/BazaarGrid.actions";
import {
    HER_BAZAAR_LOAD_API_SUCCESS,
    HerBazaarLoadApiSuccessAction,
} from "@/mk2/apps/bazaar/containers/HerBazaar/HerBazaar.actions";
import {
    MY_BAZAAR_LOAD_API_SUCCESS,
    MyBazaarLoadApiSuccessAction,
} from "@/mk2/apps/bazaar/containers/MyBazaar/MyBazaar.actions";
import {
    SHOPPING_LOAD_API_SUCCESS,
    ShoppingLoadApiSuccessAction,
} from "@/mk2/apps/bazaar/containers/Shopping/Shopping.actions";
import {
    ShoppingFilter,
    ShoppingSorting,
} from "@/mk2/apps/bazaar/containers/Shopping/Shopping.contants";
import {
    WISHLIST_LOAD_API_SUCCESS,
    WishlistLoadApiSuccessAction,
} from "@/mk2/apps/bazaar/containers/Wishlist/Wishlist.actions";
import {
    WISHLIST_INACTIVE_LOAD_API_SUCCESS,
    WishlistInactiveLoadApiSuccessAction,
} from "@/mk2/apps/bazaar/containers/WishlistInactive/WishlistInactive.actions";
import { hashFilter } from "@/mk2/apps/bazaar/helpers/filter";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { Filter } from "@/mk/bazaar/product/filter/filterUtils";

export enum NavigationType {
    MY_BAZAAR,
    HER_BAZAAR,
    BAZAAR,
    WISHLIST,
    WISHLIST_INACTIVE,
    SHOPPING,
}

interface IDs {
    [index: number]: number;
}

export interface ScrollPosition {
    x: number;
    y: number;
}

function copyIdsWithoutNewlyLoaded(ids: IDs, newlyLoaded: number[]): IDs {
    return Object.keys(ids).reduce((acc, cur) => {
        if (!newlyLoaded.includes(ids[cur])) {
            acc[cur] = ids[cur];
        }
        return acc;
    }, {});
}

export interface PerFilterState {
    // IDs of the products in the navigation - some can be undefined if not loaded yet
    ids: IDs;
    // last used filter
    filter: Filter;
    // last used shopping filter
    shoppingFilter: { sort: ShoppingSorting; filter: ShoppingFilter; username: string };
    // type of the grid - used to detect correct endpoint
    type: NavigationType;
    // total number of products
    hits: number;
    // loading state for loading more
    loadingState: LoadingState;
    // Last back url
    back: Location;
    // Last scroll position
    scroll: ScrollPosition;
}

export const initialFilterState: PerFilterState = {
    ids: {},
    filter: null,
    shoppingFilter: null,
    type: null,
    hits: null,
    loadingState: LoadingState.INIT,
    back: null,
    scroll: null,
};

type NavigationActions =
    | MyBazaarLoadApiSuccessAction
    | HerBazaarLoadApiSuccessAction
    | BazaarNavigationStoreBackAction
    | WishlistLoadApiSuccessAction
    | WishlistInactiveLoadApiSuccessAction
    | BazaarGridLoadApiSuccessAction
    | ShoppingLoadApiSuccessAction;

const perFilterReducer: Reducer<PerFilterState, NavigationActions> = (
    state = initialFilterState,
    action,
) => {
    let ids: IDs;
    let startIndex: number;

    switch (action.type) {
        case HER_BAZAAR_LOAD_API_SUCCESS:
        case BAZAAR_GRID_LOAD_API_SUCCESS:
        case MY_BAZAAR_LOAD_API_SUCCESS:
        case WISHLIST_LOAD_API_SUCCESS: {
            // Hash current filter and new filter without page to detect paging changes
            const { page, start, ...filterWithoutPaging } = action.filter;
            // Build ids array - ignore items which has moved to new position
            ids = copyIdsWithoutNewlyLoaded(state.ids, action.response.result.products);
            startIndex = isNumber(start) ? start : (page - 1) * ITEMS_PER_PAGE;
            action.response.result.products.forEach((id, index) => {
                ids[index + startIndex] = id;
            });
            return {
                ...state,
                ids,
                type:
                    action.type === HER_BAZAAR_LOAD_API_SUCCESS
                        ? NavigationType.HER_BAZAAR
                        : action.type === BAZAAR_GRID_LOAD_API_SUCCESS
                          ? NavigationType.BAZAAR
                          : action.type === MY_BAZAAR_LOAD_API_SUCCESS
                            ? NavigationType.MY_BAZAAR
                            : NavigationType.WISHLIST,
                hits: action.hits,
                filter: state.filter || filterWithoutPaging,
            };
        }
        case WISHLIST_INACTIVE_LOAD_API_SUCCESS:
            // Build ids array - ignore items which has moved to new position
            ids = copyIdsWithoutNewlyLoaded(state.ids, action.response.result.products);
            startIndex = action.start;
            action.response.result.products.forEach((id, index) => {
                ids[index + startIndex] = id;
            });
            return {
                ...state,
                ids,
                type: NavigationType.WISHLIST_INACTIVE,
                hits: action.hits,
                filter: null,
            };
        case SHOPPING_LOAD_API_SUCCESS:
            // Build ids array - ignore items which has moved to new position
            ids = copyIdsWithoutNewlyLoaded(state.ids, action.response.result.products);
            startIndex = (action.page - 1) * ITEMS_PER_PAGE;
            action.response.result.products.forEach((id, index) => {
                ids[index + startIndex] = id;
            });
            return {
                ...state,
                ids,
                type: NavigationType.SHOPPING,
                hits: action.hits,
                shoppingFilter: state.shoppingFilter || {
                    username: action.username,
                    sort: action.sort,
                    filter: action.filter,
                },
            };
        case BAZAAR_NAVIGATION_STORE_BACK:
            return {
                ...state,
                back: action.back,
                scroll: action.scroll,
            };
        default:
            return state;
    }
};

export interface State {
    // Filter hash
    active: string;
    // Navigations per filter
    filters: { [hash: string]: PerFilterState };
}

export const initialState: State = {
    active: null,
    filters: {},
};

const reducer: Reducer<State, NavigationActions | BazaarNavigationActivateAction> = (
    state = initialState,
    action,
) => {
    let hash;
    switch (action.type) {
        case HER_BAZAAR_LOAD_API_SUCCESS:
        case BAZAAR_GRID_LOAD_API_SUCCESS:
        case MY_BAZAAR_LOAD_API_SUCCESS:
        case WISHLIST_LOAD_API_SUCCESS: {
            const { page, start, ...filterWithoutPaging } = action.filter;
            hash = hashFilter(filterWithoutPaging);
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [hash]: perFilterReducer(state.filters[hash], action),
                },
                active: hash,
            };
        }
        case WISHLIST_INACTIVE_LOAD_API_SUCCESS:
            hash = "wishlist-inactive";
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [hash]: perFilterReducer(state.filters[hash], action),
                },
                active: hash,
            };
        case SHOPPING_LOAD_API_SUCCESS:
            hash = `shopping-${action.filter}-${action.sort}`;
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [hash]: perFilterReducer(state.filters[hash], action),
                },
                active: hash,
            };
        case BAZAAR_NAVIGATION_STORE_BACK:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [state.active]: perFilterReducer(state.filters[state.active], action),
                },
            };
        case BAZAAR_NAVIGATION_ACTIVATE:
            return {
                ...state,
                active: action.hash,
            };
        default:
            return state;
    }
};

export default reducer;
